import { BookOpenCheck, Building2, Check, Clock3, Play, Smartphone, UserRound } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { Logo } from '../components/Logo'
import { T, useI18n } from '../lib/i18n'
import {
  fallbackTutorials,
  fetchTutorialCatalog,
  tutorialDuration,
  type TutorialAudience,
  type TutorialLanguage,
} from '../lib/tutorialCatalog'

export function HelpPage() {
  const { t } = useI18n()
  const [params, setParams] = useSearchParams()
  const [tutorials, setTutorials] = useState(fallbackTutorials)
  const language: TutorialLanguage = params.get('lang') === 'es' ? 'es' : 'en'
  const role = params.get('role')
  const audience: 'all' | Exclude<TutorialAudience, 'both'> = role === 'business' || role === 'client' ? role : 'all'
  const visible = tutorials.filter((tutorial) => audience === 'all' || tutorial.audience === audience || tutorial.audience === 'both')

  useEffect(() => {
    let active = true
    fetchTutorialCatalog().then((rows) => {
      if (active && rows.length) setTutorials(rows)
    }).catch(() => undefined)
    return () => { active = false }
  }, [])

  function updateParam(key: string, value: string) {
    const next = new URLSearchParams(params)
    if (value === 'all') next.delete(key)
    else next.set(key, value)
    setParams(next, { replace: true })
  }

  return (
    <main className="help-page">
      <header className="legal-header"><Logo /><nav><Link to="/"><T>{'TidyPing'}</T></Link><Link to="/support"><T>{'Contact support'}</T></Link></nav></header>
      <section className="help-hero">
        <span className="help-hero__icon"><BookOpenCheck size={32} /></span>
        <span className="eyebrow"><T>{'Help & tutorials'}</T></span>
        <h1><T>{'Learn TidyPing one task at a time'}</T></h1>
        <p><T>{'Short, practical guides for cleaning companies and their clients.'}</T></p>
        <div className="help-language" aria-label={t('Tutorial language')}>
          <button className={language === 'en' ? 'selected' : ''} onClick={() => updateParam('lang', 'en')}>English</button>
          <button className={language === 'es' ? 'selected' : ''} onClick={() => updateParam('lang', 'es')}>Español</button>
        </div>
      </section>

      <section className="help-content">
        <div className="help-filters" aria-label={t('Filter tutorials')}>
          <button className={audience === 'all' ? 'selected' : ''} onClick={() => updateParam('role', 'all')}><BookOpenCheck /><T>{'All tutorials'}</T></button>
          <button className={audience === 'business' ? 'selected' : ''} onClick={() => updateParam('role', 'business')}><Building2 /><T>{'Cleaning business'}</T></button>
          <button className={audience === 'client' ? 'selected' : ''} onClick={() => updateParam('role', 'client')}><UserRound /><T>{'Client'}</T></button>
        </div>

        <div className="help-grid">
          {visible.map((tutorial, index) => {
            const videoURL = tutorial.video_urls[language] ?? tutorial.video_urls.en
            return <article className="help-card" key={tutorial.id} id={tutorial.slug}>
              <div className="help-card__video">
                {videoURL ? (
                  <video controls preload="metadata" playsInline src={videoURL} />
                ) : (
                  <div className="help-card__placeholder"><span><Play fill="currentColor" /></span><strong><T>{'Video coming soon'}</T></strong><small><T>{'The written guide is ready below.'}</T></small></div>
                )}
                <span className="help-card__number">{String(index + 1).padStart(2, '0')}</span>
              </div>
              <div className="help-card__body">
                <div className="help-card__meta"><span><Smartphone /><T>{tutorial.audience === 'client' ? 'Client' : tutorial.audience === 'both' ? 'Business & client' : 'Cleaning business'}</T></span><span><Clock3 />{tutorialDuration(tutorial.duration_seconds, language)}</span></div>
                <h2>{tutorial.title_localizations[language] ?? tutorial.title_localizations.en}</h2>
                <p>{tutorial.summary_localizations[language] ?? tutorial.summary_localizations.en}</p>
                <details>
                  <summary><T>{'Written steps'}</T></summary>
                  <ol>{(tutorial.steps_localizations[language] ?? tutorial.steps_localizations.en).map((step) => <li key={step}><Check />{step}</li>)}</ol>
                </details>
              </div>
            </article>
          })}
        </div>
      </section>
    </main>
  )
}
